import { StepProgress, type Step } from '~/ui/navigation/StepProgress';
import { CalendarIcon, LocationIcon, CheckoutIcon } from '@butter/ui/icons';
import {
  BOOKING_STEPS,
  BookingStepProvider,
  useBookingStep,
  VisualStepProvider,
  useVisualStep,
  VISUAL_STEPS,
} from '~/services/internalApi/booking/context';
import { PaymentProvider } from '~/features/payment';
import { BookingStepManager } from './components/BookingStepManager';
import { BookingTimeStep, PaymentDetailsStep, FinishAccountStep } from './steps';

/**
 * Booking Flow Page
 *
 * Multi-step booking flow with:
 * - Step 1: Date, time, and bay selection
 * - Step 2: Payment details
 * - Step 3: Finish account
 *
 * Uses step-based architecture with smooth transitions.
 */

const bookingSteps: Step[] = [
  {
    id: 'booking-time',
    label: 'Booking Time',
    icon: <CalendarIcon className="size-5" />,
  },
  {
    id: 'bay-selection',
    label: 'Bay Selection',
    icon: <LocationIcon className="size-5" />,
  },
  {
    id: 'confirmation',
    label: 'Confirmation',
    icon: <CheckoutIcon className="size-5 text-sm" />,
  },
];

/**
 * Page titles for each step
 */
const pageTitles = [
  'Make a Booking',
  'Bay Selection',
  'Payment Details',
];

/**
 * Page subtitles for each step
 */
const pageSubtitles = [
  "Let's get you set up for your next visit",
  'Choose the bay you\'d like to book',
  'Add your payment details to complete your booking',
];

/**
 * Header for the account-setup sub-step. It shares the "Confirmation" visual step
 * with payment (the stepper has three dots), so without this a guest who already
 * saved their card is told to "add your payment details" above an account form.
 */
/** The confirmation step's subtitle when nothing there asks for a card (credits S1). */
const noCardConfirmationSubtitle = 'Review and confirm your booking';

const finishAccountHeader = {
  title: 'Account Setup & Confirm',
  subtitle: 'Create your account to confirm your booking',
};

/**
 * Booking Flow Page Content
 *
 * Inner component that accesses step context.
 */
function BookingFlowPageContent() {
  const { currentStep } = useBookingStep();
  const { visualStep, paymentNeedsNoCard } = useVisualStep();

  // Get dynamic page title and subtitle based on visual step with bounds checking
  const safeStepIndex = Math.max(0, Math.min(visualStep, pageTitles.length - 1));
  const onFinishAccount = currentStep === BOOKING_STEPS.FINISH_ACCOUNT;
  const pageTitle = onFinishAccount ? finishAccountHeader.title : pageTitles[safeStepIndex];
  const pageSubtitle = onFinishAccount
    ? finishAccountHeader.subtitle
    : safeStepIndex === VISUAL_STEPS.CONFIRMATION && paymentNeedsNoCard
      ? noCardConfirmationSubtitle
      : pageSubtitles[safeStepIndex];

  return (
    <>
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
          {pageTitle}
        </h1>
        <p className="mt-2 text-sm text-gray-600">
          {pageSubtitle}
        </p>
      </div>

      {/* Progress Steps - Uses visual step instead of context step */}
      <StepProgress steps={bookingSteps} currentStep={visualStep} className="mt-16" />

      {/* Step Content - Uses actual context step for navigation */}
      <BookingStepManager currentStep={currentStep}>
        <BookingTimeStep />
        <PaymentDetailsStep />
        <FinishAccountStep />
      </BookingStepManager>
    </>
  );
}

/**
 * Booking Flow Page
 *
 * Wraps the page content with step and payment providers.
 */
export function BookingFlowPage() {
  return (
    <BookingStepProvider>
      <VisualStepProvider>
        <PaymentProvider>
          <BookingFlowPageContent />
        </PaymentProvider>
      </VisualStepProvider>
    </BookingStepProvider>
  );
}
